import { ActionModal, FormError } from '@kcftech/design-system';
import { ContentType } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { excludeSpecialChars } from 'pages/Settings/FileLibrary/shared';
import { RegisterOptions, useForm } from 'react-hook-form';
import { queryClient } from 'utils/query';

import { FileIsh } from '../FileLibraryActionsCell';

const nameRules: RegisterOptions = {
    required: 'Please enter a folder name.',
    maxLength: { value: 50, message: 'Folder name cannot exceed 50 characters.' },
    pattern: {
        value: excludeSpecialChars,
        message:
            'Folder name cannot contain any of the following characters: < > : “ / \\ | ? *',
    },
};

type FolderNameForm = {
    name: string;
};

export type FolderModalProps = Readonly<
    (
        | {
              mode: 'create';
              parentFolderId: string | undefined;
              locationId?: string;
              corporationId?: string;

              folderToEdit?: never;
          }
        | {
              mode: 'edit';
              folderToEdit?: FileIsh;

              parentFolderId?: never;
              locationId?: never;
              corporationId?: never;
          }
    ) & {
        /**
         * Callback for when edit was successful.
         */
        onSave: () => void;
        /**
         * Callback for when edit was cancelled.
         */
        onCancel: () => void;
    }
>;

/**
 * Handles creates and edits of folders
 */
export function FolderModal({
    folderToEdit,
    parentFolderId,
    corporationId,
    locationId,
    onSave,
    onCancel,
}: FolderModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<FolderNameForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: folderToEdit?.Name,
        },
    });

    const {
        mutate: saveFolderName,
        isPending: isSavingEdits,
        isError,
    } = useMutation({
        mutationFn: async (data: FolderNameForm) => {
            if (folderToEdit) {
                if (!folderToEdit.ParentFolderId) {
                    throw new Error(
                        'ParentFolderId of group being edited was not present. Are we somehow editing the root?',
                    );
                }
                await sdClient.renameFile({
                    identifier: folderToEdit.Id,
                    newName: data.name,
                });
            }
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({ type: 'success', content: 'Successfully updated folder' });
        },

        onError: () => {
            showToast({ type: 'error', content: 'Failed to update folder' });
        },
    });

    const { mutate: addFolder, isPending: isSaving } = useMutation({
        mutationFn: async (data: FolderNameForm) => {
            await sdClient.createFile({
                fileType: ContentType.Folder,
                displayName: data.name,
                parentFolderId,
                locationId,
                corporationId,
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({ type: 'success', content: 'Successfully added folder' });
        },

        onError: () => {
            showToast({ type: 'error', content: 'Failed to add folder' });
        },
    });

    return (
        <ActionModal
            data-testid="folder-modal"
            height={315}
            width={
                folderToEdit !== undefined && folderToEdit.Name.length > 45 ? 620 : 580
            }
            title={folderToEdit ? `Edit ${folderToEdit.Name}` : 'Add New Folder'}
            actionButtonText={folderToEdit ? 'Save' : 'Add'}
            actionable={
                (!isSubmitted || isValid) &&
                !(isValidating || isSubmitting) &&
                (!isSaving || !isSavingEdits)
            }
            closeButtonText="Cancel"
            onClose={onCancel}
            onAction={
                folderToEdit
                    ? handleSubmit((data) => saveFolderName(data))
                    : handleSubmit((formData) => addFolder(formData))
            }
        >
            <ValidatableLabel label="Folder Name" required error={errors.name}>
                <TextInput
                    required
                    placeholder="Enter a folder name"
                    invalid={errors.name !== undefined}
                    {...register('name', nameRules)}
                />
            </ValidatableLabel>
            <FormError
                message={isError ? `Unable to edit ${folderToEdit?.Name}` : undefined}
            />
        </ActionModal>
    );
}
